import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { trackEvent } from "../../lib/analytics";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUCCESS_ICON_SIZE = 42;

function Field({ label, required, children, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <label className="block text-[10px] tracking-overline uppercase font-semibold text-charcoal-teal/70 mb-1">
        {label}
        {required && " *"}
      </label>
      {children}
    </div>
  );
}

function inputClass() {
  return "w-full border border-sage/40 bg-cream px-3 py-2.5 text-sm text-deep-teal focus:outline-none focus:border-accent-gold";
}

function isMissingRequired(form, { requireFirm, requireRole }) {
  if (!form.first_name || !form.last_name || !form.email) return true;
  if (requireFirm && !form.firm) return true;
  if (requireRole && !form.role) return true;
  return false;
}

function labelFor(field, { requireFirm }) {
  switch (field) {
    case "email":
      return requireFirm ? "Professional Email" : "Email Address";
    case "firm":
      return requireFirm ? "Firm Name" : "Firm or Organization";
    case "role":
      return requireFirm ? "Professional Role" : "I am a…";
    default:
      return field;
  }
}

function SuccessPanel({ submitted, testIds }) {
  return (
    <div className="text-center py-10" data-testid={testIds.success}>
      <CheckCircle
        size={SUCCESS_ICON_SIZE}
        weight="light"
        className="mx-auto text-accent-gold"
      />
      <h3 className="mt-4 font-serif text-deep-teal text-2xl">Thank you.</h3>
      <p className="mt-3 text-charcoal-teal text-sm leading-relaxed">
        Your document is on its way. Check your inbox within a few minutes.
      </p>
      {submitted.download_url && (
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}${submitted.download_url}`}
          target="_blank"
          rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition"
        >
          Open the PDF
          <ArrowUpRight size={14} weight="light" />
        </a>
      )}
    </div>
  );
}

/**
 * Reusable gated-form component for downloadable lead magnets.
 *
 * Props:
 *   endpoint      — `/lead-magnet/...` path (no API_BASE prefix)
 *   trackingEvent — analytics event name fired on success
 *   eyebrow / title — UI labels
 *   submitLabel   — primary button text
 *   roles         — array of options for the role select
 *   requireFirm / requireRole — make those fields mandatory
 *   showInterest  — show the free-text interest textarea
 *   disclaimer    — small italic disclaimer text under the button
 *   testIds       — { form, success, prefix } used to keep page-level
 *                   data-testids stable across the two callers
 */
export default function GatedForm({
  endpoint,
  trackingEvent,
  eyebrow,
  title,
  submitLabel,
  roles,
  requireFirm = false,
  requireRole = false,
  showInterest = false,
  disclaimer,
  testIds,
}) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    firm: "",
    role: "",
    interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (isMissingRequired(form, { requireFirm, requireRole })) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}${endpoint}`, form);
      setSubmitted(res.data);
      trackEvent(trackingEvent);
      toast.success("Sent. Check your inbox.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessPanel submitted={submitted} testIds={testIds} />;
  }

  return (
    <div data-testid={testIds.form}>
      <div className="text-[10px] tracking-overline uppercase font-semibold text-accent-gold">
        {eyebrow}
      </div>
      <h2 className="mt-2 font-serif text-deep-teal text-3xl">{title}</h2>

      <form onSubmit={submit} className="mt-7 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="First Name" required>
            <input
              required
              type="text"
              value={form.first_name}
              onChange={update("first_name")}
              data-testid={`${testIds.prefix}-first-name`}
              className={inputClass()}
            />
          </Field>
          <Field label="Last Name" required>
            <input
              required
              type="text"
              value={form.last_name}
              onChange={update("last_name")}
              data-testid={`${testIds.prefix}-last-name`}
              className={inputClass()}
            />
          </Field>
        </div>
        <Field label={labelFor("email", { requireFirm })} required>
          <input
            required
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid={`${testIds.prefix}-email`}
            className={inputClass()}
          />
        </Field>
        <Field label={labelFor("firm", { requireFirm })} required={requireFirm}>
          <input
            type="text"
            required={requireFirm}
            value={form.firm}
            onChange={update("firm")}
            data-testid={`${testIds.prefix}-firm`}
            className={inputClass()}
          />
        </Field>
        <Field label={labelFor("role", { requireFirm })} required={requireRole}>
          <select
            required={requireRole}
            value={form.role}
            onChange={update("role")}
            data-testid={`${testIds.prefix}-role`}
            className={inputClass()}
          >
            <option value="">Select one</option>
            {roles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        {showInterest && (
          <Field label="What prompted your interest?">
            <textarea
              rows={3}
              placeholder="e.g., Client with $2M IRA considering conversion"
              value={form.interest}
              onChange={update("interest")}
              data-testid={`${testIds.prefix}-interest`}
              className={`${inputClass()} resize-none`}
            />
          </Field>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid={`${testIds.prefix}-submit`}
          className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-accent-gold !text-white text-xs tracking-overline uppercase font-medium hover:bg-accent-gold-hover transition disabled:opacity-60"
        >
          {submitting ? "Sending..." : submitLabel}
          <ArrowUpRight size={14} weight="light" />
        </button>

        {disclaimer && (
          <p className="text-[11px] text-charcoal-teal/70 italic leading-relaxed">
            {disclaimer}
          </p>
        )}
      </form>
    </div>
  );
}
